/**
 * AddTransaction Component
 * Unified entry system for Expense, Income, and Savings Goal
 * Design: Floating labels, light accent background, rounded corners, soft elevation
 * 
 * FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
 * Uses CategoryEnvelopeContext for unified category-envelope data
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Calendar, FileText, Save, X, TrendingDown, TrendingUp, Sparkles, Check, X as XIcon, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import UnifiedCategorySelector from './UnifiedCategorySelector';
import EnvelopeSelector from './EnvelopeSelector';
// FINLIST SSOT: Use CategoryEnvelopeContext as primary source, fallback to legacy
import { useCategoryEnvelope, useBudgetEnvelopes } from '@/context/CategoryEnvelopeContext';
import { 
  CATEGORY_SECTIONS, 
  getCategoryById, 
  findCategoryById,
  findSubcategory,
  getEnvelopeForCategory,
  getStatusOptions 
} from '@/constants/unifiedCategories';
import { auth } from '@/config/firebase';
import { createTransaction } from '@/services/transactionService';
import { track } from '@/services/analytics';
import { insightsAPI } from '@/services/api';
import { suggestCategory as aiSuggestCategory, suggestItems as aiSuggestItems, generateNotes as aiGenerateNotes } from '@/services/aiService';
import Tooltip from '@/components/common/Tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { getCurrencySymbol } from '@/utils/budgetHelpers';
import { toTitleCase } from '@/lib/utils';

// Transaction type constants using unified category sections
const TRANSACTION_TYPE = {
  EXPENSE: CATEGORY_SECTIONS.EXPENSE,
  INCOME: CATEGORY_SECTIONS.INCOME
};

const TRANSACTION_TYPES = [
  { id: TRANSACTION_TYPE.EXPENSE, label: 'Expense', icon: TrendingDown, color: '#EF476F', bgColor: '#FEF2F2', bgColorDark: 'rgba(239, 71, 111, 0.15)' },
  { id: TRANSACTION_TYPE.INCOME, label: 'Income', icon: TrendingUp, color: '#06D6A0', bgColor: '#ECFDF5', bgColorDark: 'rgba(6, 214, 160, 0.15)' }
];

export default function AddTransaction({ 
  onSuccess = null,
  onCancel = null,
  initialType = TRANSACTION_TYPE.EXPENSE,
  initialData = null,
  source = 'unknown'
}) {
  const navigate = useNavigate();
  const { currency } = useAuth();
  const { isDark } = useTheme();
  const currencySymbol = getCurrencySymbol(currency);
  
  // FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
  // Use try-catch for graceful fallback if context not available
  let categoryEnvelopeContext = null;
  try {
    categoryEnvelopeContext = useCategoryEnvelope();
  } catch {
    // Context not available - will use legacy fallback
    console.log('[AddTransaction] CategoryEnvelopeContext not available, using legacy fallback');
  }
  
  const [transactionType, setTransactionType] = useState(initialType);
  const [amount, setAmount] = useState(initialData?.amount || '');
  const [selectedCategory, setSelectedCategory] = useState(initialData?.category || null);
  const [selectedEnvelope, setSelectedEnvelope] = useState(initialData?.envelopeId ? { id: initialData.envelopeId } : null);
  const [description, setDescription] = useState(initialData?.description || '');
  const [date, setDate] = useState(initialData?.date || new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [isRecurring, setIsRecurring] = useState(initialData?.metadata?.recurring || false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [submissionState, setSubmissionState] = useState('pending');
  const focusEventsRef = useRef(new Set());
  const abandonmentTrackedRef = useRef(false);
  
  // FINLIST SSOT: Envelope status for selected category
  const [hasEnvelope, setHasEnvelope] = useState(true);
  const [creatingEnvelope, setCreatingEnvelope] = useState(false);
  
  // AI description enhancement state
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  
  // AI category suggestion state
  const [aiCategorySuggestion, setAiCategorySuggestion] = useState(null);
  const [loadingCategorySuggestion, setLoadingCategorySuggestion] = useState(false);
  
  // AI item suggestions state (based on category)
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [loadingItemSuggestions, setLoadingItemSuggestions] = useState(false);
  const [showItemSuggestions, setShowItemSuggestions] = useState(false);
  
  // AI notes generation state
  const [suggestedNotes, setSuggestedNotes] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);

  useEffect(() => {
    setTransactionType(initialType);
  }, [initialType]);

  // FINLIST SSOT: Auto-select category from envelope when adding expense from envelope "+" button
  // This runs once on mount to pre-populate the category AND item description from envelope
  // NO AI CALL - uses category name directly to reduce cost
  useEffect(() => {
    if (initialData?.categoryId) {
      const category = findCategoryById(initialData.categoryId);
      if (category) {
        console.log('[AddTransaction] Auto-selecting category from envelope:', initialData.categoryId, category.name);
        setSelectedCategory({
          id: category.id,
          name: category.name,
          icon: category.icon,
          emoji: category.emoji,
          color: category.color,
          description: category.description,
          subcategoryId: null,
          subcategory: null,
          customCategory: null,
          customSubcategory: null
        });
        
        // Auto-fill Item field with category name (NO AI call - reduces cost)
        // User can edit if they want a more specific description
        setDescription(category.name);
        
        // Also set the envelope if provided
        if (initialData.envelopeId) {
          setSelectedEnvelope({ id: initialData.envelopeId });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  useEffect(() => {
    track('transaction_modal_opened', {
      source,
      mode: initialType,
      stage: 'render'
    }).catch(() => {});
    focusEventsRef.current.clear();
    abandonmentTrackedRef.current = false;
    setSubmissionState('pending');
  }, [initialType, source]);

  // AI description enhancement when description is entered
  useEffect(() => {
    const getSuggestion = async () => {
      if (description.trim().length > 5 && selectedCategory && !suggestionDismissed) {
        setLoadingSuggestion(true);
        try {
          const categoryId = getCategoryIdForAI(selectedCategory.id);
          const result = await insightsAPI.enhanceDescription(
            description, 
            categoryId, 
            selectedCategory.name, 
            'expense'
          );
          
          if (result.success && result.data?.enhanced && result.data.enhanced !== description.trim()) {
            setAiSuggestion(result.data);
          } else {
            setAiSuggestion(null);
          }
        } catch (error) {
          console.error('AI suggestion error:', error);
          setAiSuggestion(null);
        } finally {
          setLoadingSuggestion(false);
        }
      } else {
        setAiSuggestion(null);
      }
    };

    const debounce = setTimeout(getSuggestion, 800);
    return () => clearTimeout(debounce);
  }, [description, selectedCategory, suggestionDismissed]);

  // AI category suggestion when description/amount changes
  useEffect(() => {
    const getCategorySuggestion = async () => {
      // Only suggest if: description exists, no category selected yet, and it's an expense
      if (description.trim().length > 3 && !selectedCategory && transactionType === TRANSACTION_TYPE.EXPENSE) {
        setLoadingCategorySuggestion(true);
        try {
          const result = await aiSuggestCategory({
            description: description.trim(),
            ...(amount && { amount: parseFloat(amount) })
          });
          
          if (result && result.suggestion && result.suggestion.confidence >= 0.65) {
            setAiCategorySuggestion(result.suggestion);
          } else {
            setAiCategorySuggestion(null);
          }
        } catch (error) {
          console.error('AI category suggestion error:', error);
          setAiCategorySuggestion(null);
        } finally {
          setLoadingCategorySuggestion(false);
        }
      } else {
        setAiCategorySuggestion(null);
      }
    };

    const debounce = setTimeout(getCategorySuggestion, 1000);
    return () => clearTimeout(debounce);
  }, [description, amount, selectedCategory, transactionType]);

  // AI item suggestions when category is selected
  useEffect(() => {
    const getItemSuggestions = async () => {
      if (selectedCategory && !description.trim()) {
        setLoadingItemSuggestions(true);
        setShowItemSuggestions(true);
        try {
          const result = await aiSuggestItems({
            category: selectedCategory.id,
            categoryName: selectedCategory.name,
            type: transactionType === TRANSACTION_TYPE.INCOME ? 'income' : 'expense',
            limit: 6
          });
          
          if (result?.data?.suggestions?.length > 0) {
            setItemSuggestions(result.data.suggestions);
          } else {
            setItemSuggestions([]);
          }
        } catch (error) {
          console.error('AI item suggestions error:', error);
          setItemSuggestions([]);
        } finally {
          setLoadingItemSuggestions(false);
        }
      } else if (!selectedCategory) {
        setItemSuggestions([]);
        setShowItemSuggestions(false);
      }
    };

    // Small delay to avoid rapid API calls
    const debounce = setTimeout(getItemSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [selectedCategory, transactionType]);

  // Hide item suggestions when user starts typing
  useEffect(() => {
    if (description.trim().length > 0) {
      setShowItemSuggestions(false);
    }
  }, [description]);

  // AI notes generation when item is entered
  useEffect(() => {
    const generateNotesForItem = async () => {
      // Only generate notes if: item entered, category selected, and notes field is empty
      if (description.trim().length > 2 && selectedCategory && !notes.trim()) {
        setLoadingNotes(true);
        try {
          const result = await aiGenerateNotes({
            itemName: description.trim(),
            category: selectedCategory.id || selectedCategory.name,
            type: transactionType === TRANSACTION_TYPE.INCOME ? 'income' : 'expense'
          });
          
          if (result?.data?.note && result.data.confidence > 0.5) {
            setSuggestedNotes(result.data.note);
          } else {
            setSuggestedNotes(null);
          }
        } catch (error) {
          console.error('AI notes generation error:', error);
          setSuggestedNotes(null);
        } finally {
          setLoadingNotes(false);
        }
      } else {
        setSuggestedNotes(null);
      }
    };

    const debounce = setTimeout(generateNotesForItem, 1200);
    return () => clearTimeout(debounce);
  }, [description, selectedCategory, notes, transactionType]);

  function emitAbandonEvent(reason = 'dismissed') {
    if (abandonmentTrackedRef.current) return;

    abandonmentTrackedRef.current = true;
    track('transaction_modal_abandoned', {
      source,
      reason,
      type: transactionType,
      hasAmount: Boolean(amount),
      hasCategory: Boolean(selectedCategory),
      hasNotes: Boolean(notes?.trim()),
      hasDescription: Boolean(description?.trim())
    }).catch(() => {});
  }

  function handleFieldFocus(field) {
    if (focusEventsRef.current.has(field)) return;
    focusEventsRef.current.add(field);

    track('transaction_field_focused', {
      source,
      field,
      type: transactionType
    }).catch(() => {});
  }

  function handleTransactionTypeChange(nextType) {
    if (nextType === transactionType) return;
    track('transaction_type_switched', {
      source,
      from: transactionType,
      to: nextType
    }).catch(() => {});
    setTransactionType(nextType);
    setSelectedCategory(null);
    setDescription('');
    setItemSuggestions([]);
    setShowItemSuggestions(false);
    setSuggestedNotes(null);
    setErrors({});
  }

  // Handle category selection from UnifiedCategorySelector
  // Transforms { categoryId, subcategoryId, ... } to full category object with id, name, etc.
  function handleCategoryChange(selectorValue) {
    if (!selectorValue || !selectorValue.categoryId) {
      setSelectedCategory(null);
      return;
    }
    
    // Handle custom category
    if (selectorValue.categoryId === '__custom__') {
      setSelectedCategory({
        id: '__custom__',
        name: selectorValue.customCategory || 'Custom',
        customCategory: selectorValue.customCategory,
        customSubcategory: selectorValue.customSubcategory,
        subcategoryId: null
      });
      // Auto-fill description with custom category name if empty
      if (!description.trim() && selectorValue.customCategory) {
        const autoDescription = selectorValue.customSubcategory 
          ? `${selectorValue.customSubcategory}` 
          : `${selectorValue.customCategory}`;
        setDescription(autoDescription);
      }
      return;
    }
    
    // Look up the full category object
    const category = findCategoryById(selectorValue.categoryId);
    if (!category) {
      console.warn('Category not found:', selectorValue.categoryId);
      setSelectedCategory(null);
      return;
    }
    
    // Get subcategory if selected
    let subcategory = null;
    if (selectorValue.subcategoryId) {
      subcategory = findSubcategory(selectorValue.categoryId, selectorValue.subcategoryId);
    }
    
    // Set the full category object
    setSelectedCategory({
      id: category.id,
      name: category.name,
      icon: category.icon,
      emoji: category.emoji,
      color: category.color,
      description: category.description,
      subcategoryId: selectorValue.subcategoryId,
      subcategory: subcategory,
      customCategory: selectorValue.customCategory,
      customSubcategory: selectorValue.customSubcategory
    });
    
    // Auto-fill description (Item name) based on category selection if empty
    // This reduces user input and improves UX - no AI needed
    if (!description.trim()) {
      let autoDescription = '';
      
      // Prefer subcategory name if selected, otherwise use category name
      if (subcategory) {
        autoDescription = subcategory.name;
      } else if (selectorValue.customSubcategory) {
        autoDescription = selectorValue.customSubcategory;
      } else {
        // Use category name as default item description
        autoDescription = category.name;
      }
      
      if (autoDescription) {
        setDescription(autoDescription);
      }
    }
    
    // Auto-fill notes with category description if notes is empty (no AI overhead)
    if (!notes.trim() && category.description) {
      setNotes(category.description);
    }
    
    // FINLIST SSOT: Check if this category has an envelope in the budget
    // Only relevant for expense transactions
    if (transactionType === TRANSACTION_TYPE.EXPENSE && categoryEnvelopeContext) {
      const { getEnvelopeIdForCategory, getEnvelopeForCategory, normalizeToCanonicalCategoryId } = categoryEnvelopeContext;
      if (getEnvelopeIdForCategory && normalizeToCanonicalCategoryId) {
        const canonicalId = normalizeToCanonicalCategoryId(category.id);
        const envelopeId = getEnvelopeIdForCategory(canonicalId);
        setHasEnvelope(!!envelopeId);
        console.log(`[AddTransaction SSOT] Category ${category.id} → envelope: ${envelopeId ? 'found' : 'not found'}`);
        
        // FINLIST ENVELOPE SYSTEM: Auto-select the matching envelope
        if (envelopeId && getEnvelopeForCategory) {
          const envelope = getEnvelopeForCategory(canonicalId);
          if (envelope) {
            setSelectedEnvelope(envelope);
            console.log(`[AddTransaction SSOT] Auto-selected envelope: ${envelope.name || envelope.id}`);
          }
        } else {
          // Clear envelope selection if no match
          setSelectedEnvelope(null);
        }
      }
    } else {
      setHasEnvelope(true); // Default to true for income or when context unavailable
      setSelectedEnvelope(null);
    }
  }

  // FINLIST ENVELOPE SYSTEM GUARDRAIL:
  // Envelope creation is ONLY allowed on the Budget page.
  // This function now redirects users to the Budget page instead of creating envelopes.
  async function handleCreateEnvelope() {
    if (!selectedCategory) return;
    
    // Instead of creating envelope here, show a toast with guidance
    toast.error(
      `No budget envelope for "${selectedCategory.name}". Please add one on the Budget page first.`,
      { duration: 5000 }
    );
    
    track('envelope_creation_blocked', {
      source,
      categoryId: selectedCategory.id,
      categoryName: selectedCategory.name,
      reason: 'only_budget_page_can_create'
    }).catch(() => {});
  }

  // Select an AI-suggested item
  function selectItemSuggestion(suggestion) {
    setDescription(suggestion.name);
    setShowItemSuggestions(false);
    // If the suggestion has a note and notes field is empty, apply it
    if (suggestion.note && !notes.trim()) {
      setNotes(suggestion.note);
    }
    track('ai_item_suggestion_selected', {
      source,
      itemName: suggestion.name,
      category: selectedCategory?.name
    }).catch(() => {});
  }

  // Accept suggested notes
  function acceptSuggestedNotes() {
    if (suggestedNotes) {
      setNotes(suggestedNotes);
      setSuggestedNotes(null);
      track('ai_notes_suggestion_accepted', {
        source,
        itemName: description,
        category: selectedCategory?.name
      }).catch(() => {});
    }
  }

  function handleCancel(reason = 'cancel_button') {
    emitAbandonEvent(reason);
    setSubmissionState('cancelled');
    onCancel?.();
  }

  // Map category IDs to backend category IDs for AI
  function getCategoryIdForAI(categoryId) {
    // Map transaction categories to backend AI categories
    const categoryMap = {
      'groceries': 'cat_groceries',
      'dining': 'cat_dining',
      'transportation': 'cat_transportation',
      'utilities': 'cat_bills',
      'shopping': 'cat_shopping',
      'entertainment': 'cat_entertainment',
      'healthcare': 'cat_healthcare',
      'education': 'cat_education',
      'housing': 'cat_housing',
      'insurance': 'cat_insurance',
      'debt': 'cat_debt',
      'savings': 'cat_savings',
      'income': 'cat_income',
      'business': 'cat_business',
      'subscription': 'cat_subscription',
      'other': 'cat_other'
    };
    return categoryMap[categoryId] || 'cat_other';
  }

  // Accept AI suggestion for description
  function acceptSuggestion() {
    if (aiSuggestion?.enhanced) {
      setDescription(aiSuggestion.enhanced);
      setAiSuggestion(null);
      setSuggestionDismissed(true);
    }
  }

  // Dismiss AI suggestion
  function dismissSuggestion() {
    setAiSuggestion(null);
    setSuggestionDismissed(true);
  }

  useEffect(() => {
    return () => {
      if (submissionState === 'submitted' || abandonmentTrackedRef.current) {
        return;
      }
      emitAbandonEvent(submissionState === 'cancelled' ? 'cancel_button' : 'dismissed');
    };
  }, [submissionState, source, transactionType, amount, selectedCategory, notes, description]);

  // Auto-suggest category when description changes
  function handleDescriptionChange(value) {
    // Apply Title Case to item name (each word capitalized)
    setDescription(toTitleCase(value));
    setSuggestionDismissed(false); // Re-enable AI suggestions when user types
    // Category suggestions are handled by the AI useEffect above
  }

  function validateForm() {
    const newErrors = {};
    const validationMeta = [];
    const numericAmount = parseFloat(amount);

    if (!amount || Number.isNaN(numericAmount) || numericAmount <= 0) {
      newErrors.amount = 'Please enter a valid amount';
      validationMeta.push({ field: 'amount', code: !amount ? 'missing' : 'invalid' });
    }

    if (!selectedCategory) {
      newErrors.category = 'Please select a category';
      validationMeta.push({ field: 'category', code: 'missing' });
    }

    if (transactionType === TRANSACTION_TYPE.EXPENSE && !description.trim()) {
      newErrors.description = 'Please enter an item name';
      validationMeta.push({ field: 'description', code: 'missing' });
    }

    if (!date) {
      newErrors.date = 'Please select a date';
      validationMeta.push({ field: 'date', code: 'missing' });
    }

    setErrors(newErrors);

    if (validationMeta.length) {
      validationMeta.forEach(meta => {
        track('transaction_validation_failed', {
          source,
          type: transactionType,
          field: meta.field,
          code: meta.code
        }).catch(() => {});
      });
      return false;
    }

    return true;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);
      
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('You must be logged in to add transactions');
      }

      const parsedAmount = parseFloat(amount);
      console.log('[AddTransaction] ===== AMOUNT PARSING DEBUG =====');
      console.log('[AddTransaction] RAW amount from input:', amount);
      console.log('[AddTransaction] typeof amount:', typeof amount);
      console.log('[AddTransaction] parsedAmount after parseFloat:', parsedAmount);
      console.log('[AddTransaction] typeof parsedAmount:', typeof parsedAmount);
      console.log('[AddTransaction] parsedAmount.toFixed(10):', parsedAmount.toFixed(10));
      console.log('[AddTransaction] Expected (if 5000):', 5000);
      console.log('[AddTransaction] Difference from 5000:', (5000 - parsedAmount).toFixed(10));
      console.log('[AddTransaction] Ratio to 5000:', (parsedAmount / 5000).toFixed(20));
      console.log('[AddTransaction] 1/1024 deduction test:', Math.abs(parsedAmount - 5000 * (1023/1024)) < 0.01 ? '✓ YES, IT IS 1/1024!' : '✗ NO');
      console.log('[AddTransaction] ===== END AMOUNT PARSING DEBUG =====');
      
      if (Number.isNaN(parsedAmount)) {
        throw new Error('Invalid amount provided');
      }

      if (!selectedCategory?.id) {
        throw new Error('Please choose a valid category');
      }

      if (!date) {
        throw new Error('Please choose a valid date');
      }

      const safeCategoryName = selectedCategory.name || 'Uncategorized';
      const safeDescription = description?.trim() || safeCategoryName;

      // FINLIST SSOT: Use CategoryEnvelopeContext for envelope lookup
      // Priority: 1) User-selected envelope, 2) Context lookup, 3) Legacy lookup
      let envelopeId = null;
      let canonicalCategoryId = selectedCategory.id;
      
      // FINLIST ENVELOPE SYSTEM: First check if user manually selected an envelope
      if (selectedEnvelope?.id && transactionType === TRANSACTION_TYPE.EXPENSE) {
        envelopeId = selectedEnvelope.id;
        console.log('[AddTransaction] Using user-selected envelope:', envelopeId);
      }
      // Try to use SSOT context if available and no envelope was manually selected
      else if (categoryEnvelopeContext && transactionType === TRANSACTION_TYPE.EXPENSE) {
        const { getEnvelopeIdForCategory, normalizeToCanonicalCategoryId } = categoryEnvelopeContext;
        if (normalizeToCanonicalCategoryId) {
          canonicalCategoryId = normalizeToCanonicalCategoryId(selectedCategory.id);
        }
        if (getEnvelopeIdForCategory) {
          envelopeId = getEnvelopeIdForCategory(canonicalCategoryId);
        }
      } else if (transactionType === TRANSACTION_TYPE.EXPENSE) {
        // Fallback to legacy method if context not available
        console.log('[AddTransaction] Using legacy envelope lookup');
        envelopeId = getEnvelopeForCategory(selectedCategory.id);
      }

      const transactionData = {
        amount: parsedAmount,
        // FINLIST SSOT: Use canonical category ID for consistent envelope matching
        categoryId: canonicalCategoryId,
        categoryType: transactionType,
        date: new Date(date).toISOString(),
        notes: notes.trim() || '',
        description: transactionType === TRANSACTION_TYPE.EXPENSE ? safeDescription : safeCategoryName,
        tags: [],
        linkedItemId: null,
        transactionType: 'manual',
        recurring: isRecurring,
        // Unified category fields (for consistency with other modules)
        subcategoryId: selectedCategory.subcategoryId || null,
        customCategory: selectedCategory.customCategory || null,
        customSubcategory: selectedCategory.customSubcategory || null,
        // FINLIST SSOT: Budget envelope mapping for atomic backend updates
        envelopeId: envelopeId,
      };

      // Use the transaction service to create the transaction
      // Backend will atomically update envelope.spent via unifiedTransactionService
      const createdTransaction = await createTransaction(transactionData);

      track('transaction_submitted', {
        source,
        type: transactionType,
        amount: parsedAmount,
        category: selectedCategory.id
      }).catch(() => {});
      setSubmissionState('submitted');
      abandonmentTrackedRef.current = true;

      // Show success message
      toast.success(`${transactionType === TRANSACTION_TYPE.EXPENSE ? 'Expense' : 'Income'} added successfully!`);

      // FINLIST FIX: Dispatch custom event to notify Budget page of transaction changes
      // This enables reactive updates without requiring a full page refresh
      // Especially important for income transactions which don't trigger envelope updates
      window.dispatchEvent(new CustomEvent('finlist:transaction-created', {
        detail: {
          type: transactionType,
          amount: parsedAmount,
          categoryId: selectedCategory.id,
          transactionId: createdTransaction?.id
        }
      }));

      // Reset form
      setAmount('');
      setSelectedCategory(null);
      setSelectedEnvelope(null);
      setDescription('');
      setNotes('');
      setIsRecurring(false);
      setErrors({});

      // Call success callback
      onSuccess?.(createdTransaction);

    } catch (error) {
      console.error('Error adding transaction:', error);
      
      // Better error messaging
      let errorMessage = 'Failed to add transaction';
      
      if (error.message?.includes('authenticated') || error.message?.includes('logged in')) {
        errorMessage = 'You must be logged in to add transactions';
      } else if (error.message?.includes('permission') || error.message?.includes('denied')) {
        errorMessage = 'You don\'t have permission to add transactions';
      } else if (error.message?.includes('network') || error.message?.includes('offline')) {
        errorMessage = 'Network error. Please check your connection';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
      
      track('transaction_submission_failed', {
        source,
        type: transactionType,
        error: error.message,
        code: error.code
      }).catch(() => {});
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-2xl mx-auto rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
      style={{
        backgroundColor: isDark ? '#1e293b' : '#F9FBFB',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold" style={{ color: isDark ? '#fff' : '#111827' }}>
            Add New {TRANSACTION_TYPES.find(t => t.id === transactionType)?.label}
          </h2>
          <p className="text-xs sm:text-sm mt-1" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>Fill in the details below</p>
        </div>
        {onCancel && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => handleCancel('close_button')}
            className="p-2 rounded-xl transition-colors touch-manipulation"
            style={{ backgroundColor: isDark ? 'transparent' : 'transparent' }}
            type="button"
            aria-label="Close add transaction dialog"
          >
            <X size={20} className="sm:w-5 sm:h-5" style={{ color: isDark ? '#9ca3af' : '#4b5563' }} />
          </motion.button>
        )}
      </div>

      {/* Type Selector - Tabs */}
      <div 
        className="flex gap-2 mb-6 sm:mb-8 p-1.5 rounded-2xl border"
        style={{
          backgroundColor: isDark ? '#334155' : '#fff',
          borderColor: isDark ? '#475569' : '#e5e7eb'
        }}
      >
        {TRANSACTION_TYPES.map(type => {
          const Icon = type.icon;
          const isActive = transactionType === type.id;
          
          return (
            <motion.button
              key={type.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleTransactionTypeChange(type.id)}
              className={`
                relative flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-3 sm:py-3.5 
                rounded-2xl font-semibold text-sm sm:text-base transition-all duration-300 
                touch-manipulation overflow-hidden
              `}
              style={{
                backgroundColor: isActive ? (isDark ? type.bgColorDark : type.bgColor) : 'transparent',
                color: isActive ? type.color : (isDark ? '#9ca3af' : '#6B7280')
              }}
            >
              {/* Active Indicator */}
              {isActive && (
                <motion.div
                  layoutId="activeTabIndicator"
                  className="absolute inset-0 rounded-2xl"
                  style={{ 
                    backgroundColor: isDark ? type.bgColorDark : type.bgColor,
                    border: `2px solid ${type.color}20`
                  }}
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                />
              )}
              
              <Icon size={18} className="sm:w-5 sm:h-5 flex-shrink-0 relative z-10" />
              <span className="truncate relative z-10">{type.label}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
        {/* Category - First Field */}
        <div className="relative" onFocusCapture={() => handleFieldFocus('category')}>
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 px-1">
            Category <span className="text-[#EF476F]">*</span>
          </label>
          <motion.div
            animate={errors.category ? { x: [0, -4, 4, -2, 2, 0] } : { x: 0 }}
            transition={{ duration: 0.35 }}
          >
            <UnifiedCategorySelector
              value={selectedCategory ? { 
                categoryId: selectedCategory.id, 
                subcategoryId: selectedCategory.subcategoryId || null,
                customCategory: selectedCategory.customCategory || null,
                customSubcategory: selectedCategory.customSubcategory || null
              } : null}
              onChange={handleCategoryChange}
              module={transactionType === TRANSACTION_TYPE.INCOME ? 'transactions-income' : 'transactions'}
              // FINLIST CONTEXT: Hide Groceries & Personal Care from direct expense entry
              // Users should use Shopping List for grocery/personal care expenses
              context={transactionType === TRANSACTION_TYPE.EXPENSE ? 'expense' : null}
              allowSubcategory={true}
              allowCustom={true}
              placeholder={`Select ${TRANSACTION_TYPES.find(t => t.id === transactionType)?.label.toLowerCase()} category`}
              className={errors.category ? 'ring-2 ring-[#EF476F]' : ''}
            />
          </motion.div>
          {errors.category && (
            <motion.p 
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-xs text-[#EF476F] font-medium px-1"
            >
              {errors.category}
            </motion.p>
          )}
          
          {/* FINLIST SSOT: No envelope warning with quick create button */}
          <AnimatePresence>
            {selectedCategory && !hasEnvelope && transactionType === TRANSACTION_TYPE.EXPENSE && (
              <motion.div
                initial={{ opacity: 0, y: -8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -8, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-800">
                      No budget envelope for "{selectedCategory.name}"
                    </p>
                    <p className="mt-1 text-xs text-amber-600">
                      This expense won't be tracked against a budget. Please add an envelope on the Budget page first.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        // Close modal and navigate to budget page
                        if (onCancel) onCancel();
                        navigate('/budget');
                      }}
                      className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Go to Budget Page
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* FINLIST ENVELOPE SYSTEM: Envelope Selector for Expenses */}
        {transactionType === TRANSACTION_TYPE.EXPENSE && (
          <div className="relative" onFocusCapture={() => handleFieldFocus('envelope')}>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 px-1">
              Budget Envelope <span className="text-gray-400 dark:text-gray-500 text-[10px]">(Recommended)</span>
            </label>
            <EnvelopeSelector
              selectedEnvelopeId={selectedEnvelope?.id || null}
              onChange={(envelope) => {
                setSelectedEnvelope(envelope);
                // Track envelope selection
                if (envelope) {
                  track('envelope_selected', {
                    source,
                    envelopeId: envelope.id,
                    envelopeName: envelope.name || envelope.label,
                    categoryId: selectedCategory?.id
                  }).catch(() => {});
                }
              }}
              placeholder="Select a budget envelope..."
              error={!selectedEnvelope && hasEnvelope === false ? 'No envelope found for this category' : null}
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 px-1">
              Link this expense to a budget envelope for tracking
            </p>
          </div>
        )}

        {/* Item/Description - Only for Expense */}
        {transactionType === TRANSACTION_TYPE.EXPENSE && (
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 px-1" htmlFor="transaction-item">
              Item <span className="text-[#EF476F]">*</span>
            </label>
            <motion.div
              animate={errors.description ? { x: [0, -4, 4, -2, 2, 0] } : { x: 0 }}
              transition={{ duration: 0.35 }}
            >
              <input
                id="transaction-item"
                type="text"
                value={description}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                onFocus={() => handleFieldFocus('description')}
                placeholder="e.g., Rice, Bread, Milk"
                className={`
                  w-full px-4 py-3.5 sm:py-4 bg-white dark:bg-slate-800 border-2 dark:border-slate-600 rounded-2xl 
                  text-base sm:text-lg font-normal text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500
                  transition-all duration-200 touch-manipulation
                  focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10
                  ${errors.description ? 'border-[#EF476F] focus:border-[#EF476F] focus:ring-[#EF476F]/10' : 'border-gray-200'}
                `}
                aria-invalid={Boolean(errors.description)}
                aria-describedby={errors.description ? 'transaction-item-error' : 'transaction-item-hint'}
              />
            </motion.div>
            <div className="mt-1.5 px-1 text-xs flex justify-between">
              <motion.p 
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className={errors.description ? 'text-[#EF476F] font-medium' : 'text-gray-400'}
                id={errors.description ? 'transaction-item-error' : 'transaction-item-hint'}
              >
                {errors.description || (loadingSuggestion ? 'AI is thinking...' : 'Name the item or vendor')}
              </motion.p>
            </div>
            
            {/* AI Suggestion */}
            <AnimatePresence>
              {aiSuggestion && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-2 p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl"
                >
                  <div className="flex items-start gap-2">
                    <Sparkles className="text-purple-600 mt-0.5 flex-shrink-0" size={16} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-purple-900 mb-1">
                        AI Suggestion:
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        {aiSuggestion.enhanced}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={acceptSuggestion}
                        className="p-1 hover:bg-green-100 rounded transition-colors"
                        title="Accept suggestion"
                      >
                        <Check size={16} className="text-green-600" />
                      </button>
                      <button
                        type="button"
                        onClick={dismissSuggestion}
                        className="p-1 hover:bg-red-100 rounded transition-colors"
                        title="Dismiss suggestion"
                      >
                        <XIcon size={16} className="text-red-600" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* AI Item Suggestions (shown when category selected but no item typed) */}
            <AnimatePresence>
              {showItemSuggestions && selectedCategory && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-3 p-3 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-xl"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="text-teal-600 flex-shrink-0" size={14} />
                    <p className="text-xs font-medium text-teal-900">
                      {loadingItemSuggestions ? 'Getting suggestions...' : `Quick picks for ${selectedCategory.name}:`}
                    </p>
                    {loadingItemSuggestions && <Loader2 size={14} className="animate-spin text-teal-600" />}
                  </div>
                  {!loadingItemSuggestions && itemSuggestions.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {itemSuggestions.map((suggestion, idx) => (
                        <motion.button
                          key={idx}
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => selectItemSuggestion(suggestion)}
                          className="px-3 py-1.5 bg-white border border-teal-300 rounded-full text-sm 
                                     text-teal-800 hover:bg-teal-100 hover:border-teal-400 
                                     transition-colors flex items-center gap-1.5 shadow-sm"
                          title={suggestion.note || 'Click to select'}
                        >
                          <span>{suggestion.name}</span>
                          {suggestion.note && (
                            <span className="text-xs text-teal-500 hidden sm:inline">✨</span>
                          )}
                        </motion.button>
                      ))}
                    </div>
                  )}
                  {!loadingItemSuggestions && itemSuggestions.length === 0 && (
                    <p className="text-xs text-teal-600">Type your own item name above</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Amount */}
        <div className="relative">
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 px-1" htmlFor="transaction-amount">
            Amount ({currencySymbol}) <span className="text-[#EF476F]">*</span>
          </label>
          <motion.div
            className="relative"
            animate={errors.amount ? { x: [0, -4, 4, -2, 2, 0] } : { x: 0 }}
            transition={{ duration: 0.35 }}
          >
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#006D77] text-xl font-bold">{currencySymbol}</span>
            <input
              id="transaction-amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onFocus={() => handleFieldFocus('amount')}
              placeholder="0.00"
              className={`
                w-full pl-10 pr-4 py-3.5 sm:py-4 bg-white dark:bg-slate-800 border-2 dark:border-slate-600 rounded-2xl 
                font-mono text-xl sm:text-2xl font-bold text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-500
                transition-all duration-200 touch-manipulation
                focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10
                ${errors.amount ? 'border-[#EF476F] focus:border-[#EF476F] focus:ring-[#EF476F]/10' : 'border-gray-200'}
              `}
              aria-invalid={Boolean(errors.amount)}
              aria-describedby={errors.amount ? 'transaction-amount-error' : undefined}
              inputMode="decimal"
            />
          </motion.div>
          {errors.amount && (
            <motion.p 
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-xs text-[#EF476F] font-medium px-1"
              id="transaction-amount-error"
            >
              {errors.amount}
            </motion.p>
          )}
        </div>

        {/* Date */}
        <div className="relative">
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 px-1" htmlFor="transaction-date">
            Date <span className="text-[#EF476F]">*</span>
          </label>
          <motion.div
            className="relative"
            animate={errors.date ? { x: [0, -4, 4, -2, 2, 0] } : { x: 0 }}
            transition={{ duration: 0.35 }}
          >
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-[#006D77]" size={20} />
            <input
              id="transaction-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              onFocus={() => handleFieldFocus('date')}
              max={new Date().toISOString().split('T')[0]}
              className={`
                w-full pl-12 pr-4 py-3.5 sm:py-4 bg-white dark:bg-slate-800 border-2 dark:border-slate-600 rounded-2xl 
                text-base sm:text-lg font-normal text-gray-900
                transition-all duration-200 touch-manipulation
                focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10
                ${errors.date ? 'border-[#EF476F] focus:border-[#EF476F] focus:ring-[#EF476F]/10' : 'border-gray-200'}
              `}
              aria-invalid={Boolean(errors.date)}
              aria-describedby={errors.date ? 'transaction-date-error' : undefined}
            />
          </motion.div>
          {errors.date && (
            <motion.p 
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-xs text-[#EF476F] font-medium px-1"
              id="transaction-date-error"
            >
              {errors.date}
            </motion.p>
          )}
        </div>

        {/* Notes (Optional) */}
        <div className="relative">
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 px-1" htmlFor="transaction-notes">
            Notes <span className="text-gray-400 dark:text-gray-500 text-[10px]">(Optional)</span>
          </label>
          <motion.div
            animate={{ x: 0 }}
          >
            <textarea
              id="transaction-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onFocus={() => handleFieldFocus('notes')}
              placeholder="Add any additional details..."
              rows={3}
              className="w-full px-4 py-3.5 sm:py-4 bg-white border-2 border-gray-200 dark:border-slate-600 rounded-2xl 
                       focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10 
                       resize-none font-normal text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500
                       transition-all duration-200 touch-manipulation"
              aria-describedby="transaction-notes-hint"
            />
          </motion.div>
          <div className="mt-1.5 px-1 text-xs flex justify-between items-center">
            <p className="text-gray-400 dark:text-gray-500" id="transaction-notes-hint">
              {loadingNotes ? 'AI is suggesting a note...' : 'Optional: add context so future you remembers'}
            </p>
            {loadingNotes && <Loader2 size={12} className="animate-spin text-gray-400" />}
          </div>
          
          {/* AI Notes Suggestion */}
          <AnimatePresence>
            {suggestedNotes && !notes.trim() && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-2 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl"
              >
                <div className="flex items-start gap-2">
                  <Sparkles className="text-amber-600 mt-0.5 flex-shrink-0" size={14} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-amber-900 mb-1">
                      Suggested note:
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {suggestedNotes}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={acceptSuggestedNotes}
                      className="p-1 hover:bg-green-100 rounded transition-colors"
                      title="Use this note"
                    >
                      <Check size={16} className="text-green-600" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSuggestedNotes(null)}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                      title="Dismiss"
                    >
                      <XIcon size={16} className="text-red-600" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Submit Error */}
        <AnimatePresence>
          {errors.submit && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-4 bg-[#FEF2F2] border-2 border-[#EF476F]/30 rounded-2xl"
            >
              <p className="text-sm text-[#EF476F] font-medium">{errors.submit}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions - Save Button */}
        <div className="pt-2">
          <Tooltip label="Log a new transaction and keep your budgets accurate." className="block w-full">
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type="submit"
              disabled={saving}
              className={`
                w-full flex items-center justify-center gap-2 px-6 py-4 sm:py-4.5
                bg-[#006D77] text-white rounded-2xl shadow-[0_2px_12px_rgba(0,109,119,0.25)]
                hover:bg-[#005661] hover:shadow-[0_4px_16px_rgba(0,109,119,0.35)]
                transition-all duration-300 font-bold text-base sm:text-lg touch-manipulation
                ${saving ? 'opacity-60 cursor-not-allowed' : ''}
              `}
            >
              <Save size={20} className="sm:w-6 sm:h-6 flex-shrink-0" />
              <span className="truncate">
                {saving ? 'Saving...' : `Save ${TRANSACTION_TYPES.find(t => t.id === transactionType)?.label}`}
              </span>
            </motion.button>
          </Tooltip>
        </div>

      </form>
    </motion.div>
  );
}
